/**
 * Diálogo para **editar los métodos de pago** de una venta del market.
 *
 * Puerto del modal de `views/market/index.php`: se reemplazan los métodos de
 * pago del comprobante (método, monto y referencia) y la suma tiene que cuadrar
 * con el total del documento. Igual que antes, el servidor rechaza la edición si
 * el turno ya está verificado, y el comprobante enviado a SUNAT conserva sus
 * datos originales.
 */

import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { Select } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';

import type { DocumentoMarket, MetodoPago, PagoEditable } from '../ventas-market.types';

/** Tolerancia al comparar la suma de los pagos con el total, como la API. */
const TOLERANCIA = 0.01;

interface FilaPago extends PagoEditable {
  /** Clave estable para React (los pagos no tienen identificador propio). */
  clave: number;
}

let siguienteClave = 1;

/** Convierte los pagos del documento en filas editables. */
function filasDe(documento: DocumentoMarket, metodos: MetodoPago[]): FilaPago[] {
  const porDefecto = metodos[0]?.id ?? '';

  if (documento.pagos.length === 0) {
    return [{ clave: siguienteClave++, metodo_id: porDefecto, monto: documento.total, referencia: '' }];
  }

  return documento.pagos.map((pago) => ({
    clave: siguienteClave++,
    metodo_id: pago.metodo_id ?? porDefecto,
    monto: pago.monto,
    referencia: pago.referencia,
  }));
}

export function EditarPagosDialogo({
  documento,
  metodos,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  documento: DocumentoMarket;
  metodos: MetodoPago[];
  guardando: boolean;
  error: string | null;
  onGuardar: (pagos: PagoEditable[]) => void;
  onCerrar: () => void;
}) {
  // El diálogo se monta de nuevo por cada documento (el listado lo renderiza sólo
  // cuando hay uno en edición), así que las filas se inicializan una sola vez.
  const [filas, setFilas] = useState<FilaPago[]>(() => filasDe(documento, metodos));

  const suma = Number(filas.reduce((total, fila) => total + (fila.monto || 0), 0).toFixed(2));
  const diferencia = Math.abs(suma - documento.total);
  const cuadra = diferencia <= TOLERANCIA;
  const algunMetodo = filas.every((fila) => fila.metodo_id !== '');
  const puedeGuardar = cuadra && algunMetodo && filas.length > 0 && !guardando;

  function cambiar(clave: number, cambios: Partial<FilaPago>) {
    setFilas((previas) =>
      previas.map((fila) => (fila.clave === clave ? { ...fila, ...cambios } : fila)),
    );
  }

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="lg"
      title="Editar métodos de pago"
      description={`${documento.numero} · ${documento.cliente || 'cliente no informado'}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onGuardar(
                filas.map((fila) => ({
                  metodo_id: fila.metodo_id,
                  monto: fila.monto,
                  referencia: fila.referencia.trim(),
                })),
              )
            }
          >
            Guardar cambios
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="warning">
          Se actualizan los métodos de pago del documento. El comprobante ya enviado a SUNAT
          mantiene sus datos originales.
        </Alert>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Total del documento
            </p>
            <p className="text-sm font-semibold text-ink-900">{formatCurrency(documento.total)}</p>
          </div>
          <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Suma de pagos
            </p>
            <p className="text-sm font-semibold text-ink-900">{formatCurrency(suma)}</p>
          </div>
        </div>

        {!cuadra ? (
          <Alert variant="error">
            La suma de los pagos ({formatCurrency(suma)}) no coincide con el total (
            {formatCurrency(documento.total)}). Diferencia: {formatCurrency(diferencia)}.
          </Alert>
        ) : null}

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="flex flex-col gap-2">
          {filas.map((fila) => (
            <div
              key={fila.clave}
              className="grid items-end gap-3 rounded-control border border-ink-100 p-3 sm:grid-cols-[1.2fr_1fr_1.2fr_auto]"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-ink-700" htmlFor={`mp-${fila.clave}`}>
                  Método de pago
                </label>
                <Select
                  id={`mp-${fila.clave}`}
                  className="h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                  value={fila.metodo_id}
                  onChange={(evento) => cambiar(fila.clave, { metodo_id: evento.target.value })}
                >
                  <option value="">— Sin método —</option>
                  {metodos.map((metodo) => (
                    <option key={metodo.id} value={metodo.id}>
                      {metodo.nombre}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-ink-700" htmlFor={`mm-${fila.clave}`}>
                  Monto
                </label>
                <input
                  id={`mm-${fila.clave}`}
                  type="number"
                  step="0.01"
                  min={0}
                  className="h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                  value={fila.monto}
                  onChange={(evento) =>
                    cambiar(fila.clave, { monto: Number(evento.target.value) || 0 })
                  }
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-ink-700" htmlFor={`mr-${fila.clave}`}>
                  Referencia
                </label>
                <input
                  id={`mr-${fila.clave}`}
                  className="h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                  placeholder="N° operación, voucher…"
                  value={fila.referencia}
                  onChange={(evento) => cambiar(fila.clave, { referencia: evento.target.value })}
                />
              </div>

              <Button
                variant="ghost"
                size="icon"
                title="Quitar este método de pago"
                aria-label="Quitar este método de pago"
                disabled={filas.length <= 1}
                onClick={() =>
                  setFilas((previas) => previas.filter((otra) => otra.clave !== fila.clave))
                }
              >
                <Trash2 className="size-4 text-red-600" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </div>

        <div>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
            onClick={() =>
              setFilas((previas) => [
                ...previas,
                {
                  clave: siguienteClave++,
                  metodo_id: metodos[0]?.id ?? '',
                  monto: 0,
                  referencia: '',
                },
              ])
            }
          >
            Agregar método de pago
          </Button>
        </div>

        {guardando ? (
          <p className="flex items-center gap-2 text-xs text-ink-500">
            <Spinner className="size-4" /> Guardando los métodos de pago…
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
